import {NgOptimizedImage} from '@angular/common';
import {Component, computed, inject, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterLink, RouterLinkActive, NavigationEnd} from '@angular/router';
import {FooterComponent} from '@components';
import {
	IonBadge,
	IonContent,
	IonHeader,
	IonIcon,
	IonItem,
	IonLabel,
	IonList,
	IonMenu,
	IonMenuToggle,
	IonRouterLink,
	IonTitle,
	IonToolbar,
	LoadingController,
	MenuController
} from '@ionic/angular/standalone';
import {TranslocoDirective} from '@jsverse/transloco';
import {RoutingUrl} from '@types';
import {addIcons} from 'ionicons';
import {
	chatbubbleEllipsesOutline,
	chatbubblesOutline,
	chevronForwardOutline,
	constructOutline,
	flagOutline,
	homeOutline,
	logOut,
	peopleOutline,
	personCircleOutline,
	personOutline,
	searchOutline,
	shieldOutline,
	statsChartOutline
} from 'ionicons/icons';
import {
	AuthenticationService,
	LoggerService,
	NotificationService,
	PresenceService,
	ProfileCompletionQueryService,
	ProfileService,
	StorageService,
	TranslateHelperService
} from '@services';

@Component({
	           selector: 'app-menu',
	           standalone: true,
	           templateUrl: './menu.component.html',
	           imports: [
			TranslocoDirective,
		           IonRouterLink,
		           RouterLink,
		           RouterLinkActive,
		           IonLabel,
		           IonIcon,
		           IonItem,
		           IonMenuToggle,
		           IonList,
		           IonBadge,
		           IonContent,
		           IonHeader,
		           NgOptimizedImage,
		           IonTitle,
		           IonToolbar,
		           IonMenu,
		           FooterComponent
	           ],
	           styleUrls: ['./menu.component.scss']
           })
export class MenuComponent implements OnInit, OnDestroy {
	public selectedIndex = 0;
	private readonly baseAppPages: RoutingUrl[] = [
		{
			id: 0,
			title: 'home',
			url: '/app/home',
			icon: 'home-outline',
			active: true,
			open: false
		},
		{
			id: 1,
			title: 'search',
			url: '/app/search',
			icon: 'search-outline',
			active: true,
			open: false
		},
		{
			id: 2,
			title: 'chat',
			url: '/app/tchat',
			icon: 'chatbubbles-outline',
			active: false,
			rooms: [],
			open: false
		},
		{
			id: 3,
			title: 'message',
			url: '/app/messages',
			icon: 'chatbubble-ellipses-outline',
			active: true,
			open: false
		},
		{
			id: 4,
			title: 'contacts',
			url: '/app/contacts',
			icon: 'people-outline',
			active: true,
			open: false
		},
		{
			id: 5,
			title: 'profile',
			url: '/app/profile',
			icon: 'person-outline',
			active: true,
			open: false
		},
		{
			id: 6,
			title: 'safety center',
			url: '/app/safety-center',
			icon: 'shield-outline',
			active: true,
			open: false
		}
	];
	private readonly adminAppPage: RoutingUrl = {
		id: 9,
		title: 'admin',
		url: '/app/admin',
		icon: 'construct-outline',
		active: true,
		pages: [
			{
				id: 10,
				title: 'profile manager',
				url: '/app/admin/profile',
				icon: 'person-circle-outline',
				active: true,
				open: false
			},
			{
				id: 11,
				title: 'room manager',
				url: '/app/admin/room',
				icon: 'chatbubbles-outline',
				active: true,
				open: false
			},
			{
				id: 12,
				title: 'tchat manager',
				url: '/app/admin/tchat',
				icon: 'chatbubble-ellipses-outline',
				active: true,
				open: false
			},
			{
				id: 13,
				title: 'report manager',
				url: '/app/admin/reports',
				icon: 'flag-outline',
				active: true,
				open: false
			}
		],
		open: false
	};
	public readonly appPages = computed(() => {
		const profile = this.currentProfile();
		if (profile?.scope === 'Admin') {
			return [...this.baseAppPages, this.adminAppPage];
		}
		return this.baseAppPages;
	});
	loggedIn = false;
	username: string | undefined;
	appPagesLoggedOut: RoutingUrl[] = [
		{
			id: 13,
			title: 'Inscription',
			url: '/register',
			icon: 'person-add',
			active: true,
			open: false
		},
		{
			id: 14,
			title: 'Connexion',
			url: '/login',
			icon: 'log-in',
			active: true,
			open: false
		}
	];
	presenceService = inject(PresenceService);
	notificationService = inject(NotificationService);
	authentificationService = inject(AuthenticationService);
	protected profileService: ProfileService;
	protected currentProfile = computed(() => this.profileService.profile());

	private readonly profileCompletionQueryService = inject(ProfileCompletionQueryService);
	public readonly profileCompletion = this.profileCompletionQueryService.completion;
	private readonly mentionsSignal = this.notificationService.listenMention();
	private readonly invitationsSignal = this.notificationService.listenInvitation();
	private readonly privateMessagesSignal = this.notificationService.listenPrivateMessageNotification();
	public readonly messageNotification = computed(() => this.mentionsSignal()?.length ?? 0);
	public readonly contactNotification = computed(() => this.invitationsSignal()?.length ?? 0);
	public readonly privateMessageNotification = computed(() => {
		const notifications = this.privateMessagesSignal();
		if (!notifications || notifications.length === 0) {
			return 0;
		}
		const roomIds = new Set(notifications.map(notification => notification.rid).filter(Boolean));
		return roomIds.size;
	});
	private menuController: MenuController;
	private router: Router;
	private storageService: StorageService;
	private loadingCtrl: LoadingController;
	public translateService: TranslateHelperService;
	private logger: LoggerService;
	private routerEventsSub?: { unsubscribe: () => void };

	constructor() {
		this.profileService = inject(ProfileService);
		this.menuController = inject(MenuController);
		this.router = inject(Router);
		this.storageService = inject(StorageService);
		this.loadingCtrl = inject(LoadingController);
		this.translateService = inject(TranslateHelperService);
		this.logger = inject(LoggerService);

		this.loggedIn = true;

			addIcons({
				         homeOutline,
				         chevronForwardOutline,
				         constructOutline,
				         flagOutline,
				         shieldOutline,
				         statsChartOutline,
				         personCircleOutline,
				         personOutline,
				         peopleOutline,
				         searchOutline,
				         chatbubbleEllipsesOutline,
				         chatbubblesOutline,
				         logOut
			         });

	}

	onMenuWillOpen(): void {
		const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
		if (activeElement instanceof HTMLElement && typeof activeElement.blur === 'function') {
			activeElement.blur();
		}
	}

	ngOnInit() {
		this.selectedIndex = 0;
		// Use router events instead of window.location.hash for better compatibility
		this.routerEventsSub = this.router.events.subscribe((event) => {
			if (!(event instanceof NavigationEnd)) {
				return;
			}
			const url = this.router.url;
			const parts = url.split('/').filter(Boolean);
			const appIndex = parts.indexOf('app');
			const path = appIndex >= 0 ? parts[appIndex + 1] : (parts[1] ?? parts[0]);
			if (path !== undefined) {
				const pages = this.appPages();
				this.selectedIndex = pages.findIndex((page) => {
					const pageParts = page.url.split('/').filter(Boolean);
					const pageAppIndex = pageParts.indexOf('app');
					const pagePath = pageAppIndex >= 0 ? pageParts[pageAppIndex + 1] : pageParts[0];
					return pagePath?.toLowerCase() === path.toLowerCase();
				});
				if (this.selectedIndex < 0) {
					this.selectedIndex = 0;
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.routerEventsSub?.unsubscribe();
	}

	async logout() {
		this.storageService.clear();

		const loading = await this.loadingCtrl.create({
			                                              message: await this.translateService.instant(`Deconnexion en cours`)
			                                              });
		await loading.present();
		await this.presenceService.signOut();
		this.profileService.Disconnect().then(() => {
			this.username = '';
			loading.dismiss();
			this.menuController.enable(false);
			this.router.navigateByUrl(this.translateService.localize('/landing'))
				.then(() => this.logger.info('User logged out'))
				.catch((error) => this.logger.error('Logout navigation failed', error));
		});
	}

	rootHome(): void {
		this.router.navigateByUrl(this.translateService.localize('/app/home')).then();
	}
}
